import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { KpiCard } from "../components/KpiCard";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtNum } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function BlockedUnits() {
  const { projectsQS } = usePeriod();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    const qs = `page=${page}&page_size=${pageSize}${projectsQS}`;
    api.get(`/kpi/blocked-units?${qs}`).then((r) => setData(r.data));
  }, [projectsQS, page]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [projectsQS]);

  if (!data) return <><Header title="Blocked Units" /><div className="p-6">Loading...</div></>;

  const pg = data.pagination || { page: 1, total_pages: 1, total: data.items.length };

  return (
    <>
      <Header title="Blocked Units" subtitle="Aging · Value at Risk · Conversion" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <AIInsightBanner context={{ kpis: { blocked_units: data.total_count, gross_sales: data.total_value } }} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total Blocked" value={fmtNum(data.total_count)} dark />
          <KpiCard label="Value at Risk" value={fmtINR(data.total_value, { short: true })} accent />
          <KpiCard label="Over 60 days" value={fmtNum(data.aging_buckets.filter(b => b.bucket === "61-90" || b.bucket === "90+").reduce((a, b) => a + b.count, 0))} />
          <KpiCard label="Projects Affected" value={fmtNum(data.by_project.length)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Days Blocked Distribution</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.aging_buckets}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Blocked Units" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Value at Risk by Aging</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.aging_buckets}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(1)}Cr`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
                <Bar dataKey="value" fill="#0F1D32" radius={[4, 4, 0, 0]} name="Value at Risk" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Project-wise Blocked Inventory</div>
          <table className="w-full text-xs" data-testid="blocked-by-project">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Project</th>
                <th className="text-right px-4 py-2">Blocked Units</th>
                <th className="text-right px-4 py-2">Value at Risk</th>
              </tr>
            </thead>
            <tbody>
              {data.by_project.map((r) => (
                <tr key={r.project_name} className="border-t border-slate-100">
                  <td className="px-4 py-2 truncate max-w-[260px]">{r.project_name}</td>
                  <td className="numeric-cell px-4 py-2">{r.count}</td>
                  <td className="numeric-cell px-4 py-2 font-semibold">{fmtINR(r.value, { short: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold">Conversion Risk · Aged Items</div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono" data-testid="blocked-page-info">
                Page {pg.page} of {pg.total_pages} · {pg.total} items
              </span>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="p-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-50"
                data-testid="blocked-prev-page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage(Math.min(pg.total_pages, page + 1))}
                disabled={page >= pg.total_pages}
                className="p-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-slate-50"
                data-testid="blocked-next-page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="blocked-items-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Project</th>
                  <th className="text-left px-4 py-2">Unit</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Blocked By</th>
                  <th className="text-right px-4 py-2">Days</th>
                  <th className="text-right px-4 py-2">Est. Value</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 truncate max-w-[180px]">{i.project_name}</td>
                    <td className="px-4 py-2 font-mono">{i.unit_no}</td>
                    <td className="px-4 py-2">{i.block_status}</td>
                    <td className="px-4 py-2">{i.blocked_by}</td>
                    <td className={`numeric-cell px-4 py-2 font-semibold ${i.days_blocked > 60 ? "text-red-600" : i.days_blocked > 30 ? "text-amber-600" : ""}`}>
                      {i.days_blocked}
                    </td>
                    <td className="numeric-cell px-4 py-2">{fmtINR(i.estimated_value, { short: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
